import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const GROUPS = [
  { title: "Application", items: [
    { key: "campus_france", label: "Create Campus France account" },
    { key: "transcripts", label: "Request official transcripts (English + certified translation)" },
    { key: "recommendations", label: "Secure 2 letters of recommendation" },
    { key: "personal_statement", label: "Draft & polish personal statement" },
    { key: "language_test", label: "Take TOEFL/IELTS or DELF/DALF if required" },
    { key: "submit_apps", label: "Submit applications (target: 3–5 schools)" },
  ]},
  { title: "Post-acceptance", items: [
    { key: "accept_offer", label: "Accept offer & pay tuition deposit" },
    { key: "cvec", label: "Pay CVEC (student life fee, ~€103)" },
    { key: "visa_appt", label: "Book VLS-TS visa appointment" },
    { key: "financial_proof", label: "Prepare financial proof (~€615/month coverage)" },
  ]},
  { title: "Move-in", items: [
    { key: "housing_lease", label: "Sign lease + set up Visale guarantor" },
    { key: "home_insurance", label: "Get home insurance (assurance habitation)" },
    { key: "bank_account", label: "Open French bank account, get RIB" },
    { key: "anef_validation", label: "Validate visa on ANEF within 3 months" },
    { key: "caf_application", label: "Apply for CAF (housing subsidy)" },
    { key: "secu_enrollment", label: "Confirm Sécurité sociale enrollment" },
    { key: "sim_card", label: "Buy prepaid SIM / phone plan" },
    { key: "transit_pass", label: "Set up transit pass (Navigo Imagine'R etc.)" },
  ]},
];

export default function Apply() {
  const { user } = useAuth();
  const [items, setItems] = useState({});
  const total = GROUPS.reduce((s, g) => s + g.items.length, 0);
  const done = Object.values(items).filter(Boolean).length;

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/tracker");
        setItems(data.items || {});
      } catch {}
    })();
  }, [user]);

  const toggle = async (key) => {
    const next = !items[key];
    setItems((prev) => ({ ...prev, [key]: next }));
    if (!user) { toast.info("Log in to save your progress."); return; }
    try { await api.post("/tracker", { key, checked: next }); }
    catch { toast.error("Save failed."); }
  };

  return (
    <div className="pt-28 md:pt-32 pb-24 min-h-screen" data-testid="page-apply">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-6">
          <span className="rule-gold" /><span className="eyebrow">03 · Apply</span>
        </div>
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <h1 className="md:col-span-8 serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            Every deadline. <em className="italic text-[#b8892b]">One tracker.</em>
          </h1>
          <div className="md:col-span-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#4a5570]">Progress</div>
            <div className="serif text-5xl text-[#b8892b] mt-1">{done}<span className="text-[#0d1b3e] text-2xl">/{total}</span></div>
            <div className="mt-3 h-[3px] bg-[#0d1b3e]/10">
              <motion.div className="h-full bg-[#b8892b]" initial={{ width: 0 }} animate={{ width: `${(done/total)*100}%` }} />
            </div>
          </div>
        </div>

        {!user && (
          <div className="mt-8 bg-white border border-[#b8892b]/40 p-5 text-sm text-[#0d1b3e]" data-testid="apply-login-notice">
            <span className="font-semibold">Not logged in.</span> You can play with the tracker below, but <Link to="/login" className="text-[#b8892b] underline">log in</Link> to save your progress across visits.
          </div>
        )}

        <div className="mt-12 space-y-14">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="flex items-center gap-4 border-b border-[#0d1b3e]/15 pb-3 mb-6">
                <h2 className="serif text-3xl text-[#0d1b3e]">{g.title}</h2>
                <div className="text-xs uppercase tracking-[0.2em] text-[#4a5570]">
                  {g.items.filter(i => items[i.key]).length} / {g.items.length}
                </div>
              </div>
              <div className="space-y-2">
                {g.items.map((it) => {
                  const checked = !!items[it.key];
                  return (
                    <button key={it.key} onClick={() => toggle(it.key)}
                      data-testid={`tracker-item-${it.key}`}
                      className={`w-full text-left flex items-start gap-4 p-4 border transition-colors ${checked ? "bg-[#0d1b3e] text-white border-[#0d1b3e]" : "bg-white border-[#0d1b3e]/15 hover:border-[#b8892b]"}`}>
                      <span className={`mt-0.5 w-6 h-6 flex items-center justify-center border ${checked ? "bg-[#b8892b] border-[#b8892b] text-white" : "border-[#0d1b3e]/40"}`}>
                        {checked && <Check size={14} />}
                      </span>
                      <span className={`flex-1 ${checked ? "line-through opacity-70" : ""}`}>{it.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
